import { ChevronRight, Scale, Users } from "lucide-react";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import type { HomePageData } from "../../services/HomeService";
import GluceraNavigationRoutes from "../../utils/navigationRoutes";
import {
  buildWeightActivitySubtitle,
  getPoweringItems,
  getWeightPoweringItem,
} from "../../utils/measurePageMappers";
import {
  measureCardHeading,
  measureCardTitle,
  measureCardValue,
  gluceraHomeMeta,
} from "../../utils/gluceraTypography";
import { homeAccentIconShell, homeAccentLink, homeAccentText, homeCardTopWash } from "../../utils/homeBlueTheme";
import WeightManagementCard from "./WeightManagementCard";
import { cn } from "../../lib/utils";

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export interface HomePostMealExtrasSectionProps {
  homeData?: HomePageData;
  onOpenFamilyGroups: () => void;
}

const HomePostMealExtrasSection = ({
  homeData,
  onOpenFamilyGroups,
}: HomePostMealExtrasSectionProps) => {
  const navigate = useNavigate();

  const weightData = homeData?.["Weight Management"];
  const poweringItems = getPoweringItems(
    homeData as Parameters<typeof getPoweringItems>[0],
  );
  const weightPowering = getWeightPoweringItem(poweringItems);

  const familyGroups = homeData?.["Family Health"]?.groups ?? [];
  const familyGroup = useMemo(() => {
    const homeGroup = familyGroups[0];
    if (homeGroup?.id && UUID_RE.test(homeGroup.id)) {
      return {
        id: homeGroup.id,
        name: homeGroup.name ?? "Family",
        member_count:
          homeGroup.summary?.members_total ?? homeGroup.members?.length ?? 0,
      };
    }
    return null;
  }, [familyGroups]);

  const groupsCount = familyGroups.length;
  const familyGroupSubtitle =
    groupsCount === 0
      ? "NO GROUP YET · TAP TO MANAGE"
      : groupsCount === 1 && familyGroup
        ? `${familyGroup.name.toUpperCase()} • ${familyGroup.member_count} ${
            familyGroup.member_count === 1 ? "MEMBER" : "MEMBERS"
          }`
        : `${groupsCount} GROUPS · TAP TO MANAGE`;

  const weightSubtitle = buildWeightActivitySubtitle(weightPowering);
  const weightValue = weightPowering?.value?.trim() || "—";

  return (
    <section className="space-y-2 pb-1" aria-label="Weight and family">
      {weightData ? (
        <WeightManagementCard data={weightData} summaryOnly />
      ) : (
        <div className="relative overflow-hidden rounded-2xl border border-border/80 bg-card shadow-sm backdrop-blur-sm dark:border-border/60">
          <div
            className={cn("pointer-events-none absolute inset-x-0 top-0 h-12 bg-gradient-to-b", homeCardTopWash)}
            aria-hidden
          />
          <button
            type="button"
            className="relative flex w-full items-start gap-3 p-4 text-left outline-none ring-offset-background transition-colors hover:bg-muted/30 focus-visible:ring-2 focus-visible:ring-ring"
            onClick={() =>
              navigate(`/glucera${GluceraNavigationRoutes.WEIGHT_MANAGEMENT_DETAIL}`)
            }
          >
            <div className={cn("flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-[#737DFE]/25", homeAccentIconShell)}>
              <Scale
                className={cn("h-[22px] w-[22px]", homeAccentText)}
                strokeWidth={1.85}
                aria-hidden
              />
            </div>
            <div className="min-w-0 flex-1 pt-0.5">
              <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                <span className={measureCardHeading}>Weight</span>
                <span className={measureCardValue}>{weightValue}</span>
              </div>
              {weightSubtitle ? (
                <p className={`mt-1.5 ${gluceraHomeMeta}`}>{weightSubtitle}</p>
              ) : null}
            </div>
            <ChevronRight
              className="mt-1 h-5 w-5 shrink-0 text-muted-foreground"
              strokeWidth={2}
              aria-hidden
            />
          </button>
        </div>
      )}

      <button
        type="button"
        className="flex w-full items-center gap-3 rounded-2xl border border-border bg-card px-4 py-3 text-left shadow-sm outline-none ring-offset-background focus-visible:ring-2 focus-visible:ring-ring"
        onClick={onOpenFamilyGroups}
      >
        <span className={cn("flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-[#737DFE]/25", homeAccentIconShell)}>
          <Users
            className={cn("h-[22px] w-[22px]", homeAccentText)}
            strokeWidth={1.75}
            aria-hidden
          />
        </span>
        <span className="min-w-0 flex-1">
          <span className={`block ${measureCardTitle}`}>Family group</span>
          <span className={`mt-0.5 block ${gluceraHomeMeta}`}>
            {familyGroupSubtitle}
          </span>
        </span>
        <ChevronRight
          className="h-5 w-5 shrink-0 text-muted-foreground"
          strokeWidth={2}
          aria-hidden
        />
      </button>
    </section>
  );
};

export default HomePostMealExtrasSection;
